import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useIsAdmin, usePendingVendors, useApprovedVendors, useAuthRecoveryStats } from '@/hooks/useAdmin';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, Users, CheckCircle, Clock, AlertTriangle, CreditCard, Settings, KeyRound } from 'lucide-react';
import PendingVendorsList from '@/components/admin/PendingVendorsList';
import AdminPayments from '@/components/admin/AdminPayments';
import AdminPaymentSettings from '@/components/admin/AdminPaymentSettings';
import { usePendingPaymentsCount } from '@/hooks/useAdminPayments';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { data: pendingVendors } = usePendingVendors();
  const { data: approvedVendors } = useApprovedVendors();
  const { data: pendingPaymentsCount = 0 } = usePendingPaymentsCount();
  const { data: recoveryStats } = useAuthRecoveryStats(30);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!adminLoading && isAdmin === false) {
      navigate('/');
    }
  }, [isAdmin, adminLoading, navigate]);

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Skeleton className="h-12 w-12 rounded-full mx-auto" />
          <Skeleton className="h-4 w-32 mx-auto" />
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  const pendingCount = pendingVendors?.length || 0;
  const approvedCount = approvedVendors?.length || 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Panel de Administración</h1>
                <p className="text-sm text-muted-foreground">Gestión de proveedores</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              ← Volver al inicio
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pendientes de Aprobación
              </CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">{pendingCount}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Proveedores Aprobados
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{approvedCount}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Proveedores
              </CardTitle>
              <Users className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{pendingCount + approvedCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* 010 · Métrica de recuperación de contraseña (últimos 30 días) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Recuperaciones solicitadas (30d)
              </CardTitle>
              <KeyRound className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{recoveryStats?.requested ?? '—'}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Recuperaciones completadas (30d)
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{recoveryStats?.completed ?? '—'}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Tasa de completación
              </CardTitle>
              <Shield className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {recoveryStats ? `${recoveryStats.completion_rate}%` : '—'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Alert */}
        {pendingCount > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <p className="text-amber-800">
                  Hay <strong>{pendingCount}</strong> proveedor(es) esperando tu aprobación.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pending" className="relative">
              Pendientes
              {pendingCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-amber-500 text-white rounded-full">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">Aprobados</TabsTrigger>
            <TabsTrigger value="payments" className="relative">
              <CreditCard className="h-4 w-4 mr-1.5" />
              Pagos
              {pendingPaymentsCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-amber-500 text-white rounded-full">
                  {pendingPaymentsCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="payment-settings">
              <Settings className="h-4 w-4 mr-1.5" />
              Config pagos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Proveedores Pendientes</CardTitle>
                <CardDescription>
                  Revisa y aprueba los nuevos proveedores que desean aparecer en el directorio.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PendingVendorsList />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approved">
            <Card>
              <CardHeader>
                <CardTitle>Proveedores Aprobados</CardTitle>
                <CardDescription>
                  Lista de proveedores activos en el directorio público.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {approvedVendors && approvedVendors.length > 0 ? (
                  <div className="space-y-3">
                    {approvedVendors.map((vendor) => (
                      <div 
                        key={vendor.id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {vendor.image && (
                            <img 
                              src={vendor.image}
                              alt={vendor.name}
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                          )}
                          <div>
                            <p className="font-medium">{vendor.name}</p>
                            <p className="text-sm text-muted-foreground">{vendor.location}</p>
                          </div>
                        </div>
                        <a
                          href={`/vendor/${vendor.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline"
                        >
                          Ver perfil →
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No hay proveedores aprobados aún.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Pagos</CardTitle>
                <CardDescription>
                  Revisa los comprobantes y aprueba o rechaza cada pago. Aprobar habilita la publicación de la invitación.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminPayments />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payment-settings">
            <Card>
              <CardHeader>
                <CardTitle>Configuración de pagos</CardTitle>
                <CardDescription>
                  Precio de publicación, tasa USD→Bs y datos bancarios que ven las parejas.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminPaymentSettings />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
